import type { ReactElement } from 'react';
import { Avatar, type BoxProps, Circle, HStack, Link, Stack, Text } from '@chakra-ui/react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

type TicketListItemProps = Omit<BoxProps, 'id'> & {
  id: string;
  baseUrl: string;
  requesterName: string;
  requesterType: string;
  subject: string;
  relativeTime: string;
  hasUnread: boolean;
  assignedToName?: string;
  requesterTypeIcon?: ReactElement;
};

export function TicketListItem({
  id,
  baseUrl,
  requesterName,
  requesterType,
  subject,
  relativeTime,
  hasUnread,
  assignedToName,
  requesterTypeIcon,
  ...props
}: TicketListItemProps) {
  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const ticketUrl = queryString.length > 0 ? `${baseUrl}/${id}?${queryString}` : `${baseUrl}/${id}`;

  return (
    <Link as={RouterLink} to={ticketUrl} _hover={{ textDecoration: 'none' }}>
      <HStack
        align="flex-start"
        gap="3"
        px="4"
        py="3"
        _hover={{ bg: 'bg.subtle' }}
        cursor="pointer"
        borderBottom="1px"
        borderColor="border.default"
        {...props}
      >
        <Avatar size="sm" name={requesterName} />

        <Stack spacing="0" fontSize="sm" flex="1" isTruncated>
          <HStack spacing="1" align="flex-start">
            <HStack spacing="1" flex="1" minWidth="0">
              <Text fontWeight="600" noOfLines={1}>
                {requesterName}
              </Text>
              {requesterTypeIcon}
              <Text fontWeight="normal" color="fg.subtle">
                {requesterType}
              </Text>
            </HStack>
            <Stack spacing="0" align="flex-end">
              <HStack spacing="1">
                <Text color="fg.subtle" fontSize="xs" fontWeight="normal">
                  {relativeTime}
                </Text>
                {hasUnread === true ? (
                  <Circle
                    size="0.5rem"
                    bg="blue.500"
                    role="status"
                    aria-label="Unread ticket indicator"
                  />
                ) : null}
              </HStack>
            </Stack>
          </HStack>

          <HStack spacing="1" align="flex-start" justify="space-between">
            <Text
              fontSize="xs"
              lineHeight="5"
              fontWeight="medium"
              color="fg.subtle"
              flex="1"
              noOfLines={1}
            >
              {subject}
            </Text>
            {assignedToName !== undefined && (
              <HStack spacing="1" flexShrink={0}>
                <Text fontSize="2xs" color="fg.muted" fontWeight="normal">
                  Assigned to:
                </Text>
                <Avatar size="2xs" name={assignedToName} />
                <Text fontSize="2xs" color="fg.muted" fontWeight="medium">
                  {assignedToName}
                </Text>
              </HStack>
            )}
          </HStack>
        </Stack>
      </HStack>
    </Link>
  );
}

export default TicketListItem;
